import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Download, FileText, PenLine } from 'lucide-react';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Button } from '@/shared/ui/Button';
import { Skeleton } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast/useToast';
import { formatDate } from '@/shared/utils/format';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useReviewEcpMutation, useSafetyRecords } from '../hooks';
import { AttestationFooter, PnTag, SafetyLoadError } from '../components/SafetyBits';
import styles from './SafetyRecordsPage.module.css';

export function SafetyRecordsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, isError, refetch } = useSafetyRecords();
  const reviewEcp = useReviewEcpMutation();
  const [ecpOpen, setEcpOpen] = useState(false);

  if (isError) {
    return (
      <div className={patterns.page}>
        <PageHeader title="Records" />
        <SafetyLoadError onRetry={() => refetch()} />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className={patterns.page}>
        <Skeleton height={80} />
        <Skeleton height={300} />
      </div>
    );
  }

  const handleReview = async () => {
    try {
      await reviewEcp.mutateAsync();
      toast.success('Exposure Control Plan review recorded');
    } catch {
      toast.error('Could not record the review');
    }
  };

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Records"
        subtitle="Proof of training delivered, acknowledged, and documented — retained 3 years (HIPAA-related records 6 years)."
      />

      {/* Binder CTA */}
      <div className={`${patterns.panel} ${styles.binderCta}`}>
        <div>
          <div className={styles.binderTitle}>Inspection-ready binder</div>
          <div className={styles.binderSub}>
            One document bundling training completion records, equipment logs, the Exposure Control
            Plan, and the requirements table — what an OSHA or board inspector asks to see. Print it
            or save as PDF.
          </div>
        </div>
        <Button
          variant="primary"
          leftIcon={<Download size={14} aria-hidden />}
          onClick={() => navigate('/safety/records/binder')}
        >
          Generate binder
        </Button>
      </div>

      {/* Completion records */}
      <section>
        <div className={patterns.sectionHeader}>
          <span className={patterns.sectionTitle}>Completion records</span>
          <span className={patterns.sectionFootnote}>
            {data.completions.length} on file · every record itemizes its topics
          </span>
        </div>
        <div className={patterns.gridTable}>
          <div className={`${patterns.gridTableHeader} ${styles.recordGrid}`}>
            <span>Team member</span>
            <span>Course</span>
            <span>Completed</span>
            <span>Score</span>
            <span>Signature</span>
          </div>
          {data.completions.map((record) => (
            <div key={record.id} className={`${patterns.gridTableRow} ${styles.recordGrid}`}>
              <div>
                <div className={patterns.cellName}>{record.staffName}</div>
                <div className={patterns.cellSmMuted}>{record.roleLabel}</div>
              </div>
              <div>
                <div className={patterns.cellSecondary}>
                  {record.courseTitle} · v{record.courseVersion}
                </div>
                <div className={patterns.cellSmMuted}>
                  {record.kind === 'onboarding' ? 'New-hire onboarding · ' : ''}
                  {record.topics.length} topics
                </div>
              </div>
              <span className={patterns.cellMuted}>{formatDate(record.completedAt)}</span>
              <span className={patterns.cellSecondary}>{Math.round(record.quizScore * 100)}%</span>
              <span>
                {record.signed ? (
                  <span className={patterns.pillSuccess}>
                    <PenLine size={11} aria-hidden /> E-signed
                  </span>
                ) : (
                  <span className={patterns.pillWarning}>Awaiting</span>
                )}
              </span>
            </div>
          ))}
          {data.completions.length === 0 && (
            <div className={patterns.gridTableEmpty}>
              No completions yet — records appear here as your team finishes training.
            </div>
          )}
        </div>
      </section>

      {/* Exposure Control Plan */}
      <section>
        <div className={patterns.sectionHeader}>
          <span className={patterns.sectionTitle}>Exposure Control Plan</span>
          <span className={patterns.sectionFootnote}>Reviewed &amp; updated at least annually</span>
        </div>
        <div className={patterns.panel}>
          {data.ecp ? (
            <>
              <div className={styles.ecpHead}>
                <FileText size={16} aria-hidden className={styles.ecpIcon} />
                <div className={styles.ecpMeta}>
                  <span className={patterns.cellName}>
                    Version {data.ecp.version}
                    {data.ecp.reviewedAt
                      ? ` · reviewed ${formatDate(data.ecp.reviewedAt)}`
                      : ' · not yet reviewed'}
                  </span>
                  <span className={patterns.cellSmMuted}>
                    {data.ecp.nextReview
                      ? `Next review due ${formatDate(data.ecp.nextReview)}`
                      : 'Annual review'}
                  </span>
                </div>
                <div className={styles.ecpActions}>
                  <Button variant="secondary" size="sm" onClick={() => setEcpOpen((o) => !o)}>
                    {ecpOpen ? (
                      <>
                        Hide plan <ChevronUp size={13} aria-hidden />
                      </>
                    ) : (
                      <>
                        View plan <ChevronDown size={13} aria-hidden />
                      </>
                    )}
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleReview}
                    loading={reviewEcp.isPending}
                  >
                    Mark reviewed
                  </Button>
                </div>
              </div>
              {ecpOpen && (
                <div className={styles.ecpSections}>
                  {data.ecp.content.sections.map((section) => (
                    <div key={section.heading} className={styles.ecpSection}>
                      <h3 className={styles.ecpHeading}>{section.heading}</h3>
                      <p className={styles.ecpBody}>{section.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className={styles.emptyNote}>
              The Exposure Control Plan is generated when you complete setup.
            </p>
          )}
        </div>
      </section>

      {/* What applies here — CA overlay */}
      <section>
        <div className={patterns.sectionHeader}>
          <span className={patterns.sectionTitle}>What applies here — California</span>
          <span className={patterns.sectionFootnote}>
            Federal core is uniform everywhere; only these vary by state. Tagged by what we do.
          </span>
        </div>
        <div className={patterns.gridTable}>
          <div className={`${patterns.gridTableHeader} ${styles.overlayGrid}`}>
            <span>Requirement</span>
            <span>Source</span>
            <span>Citation</span>
            <span>PracticeNova</span>
          </div>
          {data.caOverlay.map((row) => (
            <div key={row.requirement} className={`${patterns.gridTableRow} ${styles.overlayGrid}`}>
              <span className={patterns.cellName}>{row.requirement}</span>
              <span className={patterns.cellMuted}>{row.source}</span>
              <span className={patterns.cellSmMuted}>
                {row.citation} · verified {formatDate(row.lastVerified)}
              </span>
              <span>
                <PnTag action={row.pnAction} />
              </span>
            </div>
          ))}
        </div>
      </section>

      <AttestationFooter text={data.attestation} />
    </div>
  );
}
